import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import '../styles/pages/PrivacyPage.css';

const CookiesPage = () => {
  return (
    <>
      <Helmet>
        <title>Política de Cookies — LifeUp</title>
        <meta
          name="description"
          content="Política de Cookies do LifeUp — quais cookies usamos, finalidades, prazos de retenção e como gerenciar suas preferências."
        />
        <link rel="canonical" href="https://lifyup.com.br/cookies" />
      </Helmet>

      <div className="legal-page">
        <Header />

        <main className="legal-content">
          <div className="container">
            <header className="legal-header">
              <h1 className="legal-title">Política de Cookies</h1>
              <p className="legal-updated">Versão 1.0 — vigente a partir de 1º de junho de 2026</p>
              <p className="legal-updated">Última atualização: 1º de junho de 2026</p>
            </header>

            <nav className="legal-toc" aria-label="Sumário">
              <h3>Sumário</h3>
              <ol>
                <li><a href="#sobre">O que são cookies</a></li>
                <li><a href="#por-que">Por que usamos cookies</a></li>
                <li><a href="#tipos">Tipos de cookies utilizados</a></li>
                <li><a href="#tabela">Lista detalhada de cookies</a></li>
                <li><a href="#terceiros">Cookies de terceiros</a></li>
                <li><a href="#mobile">Cookies no aplicativo móvel</a></li>
                <li><a href="#gerenciar">Como gerenciar e remover cookies</a></li>
                <li><a href="#consentimento">Consentimento e revogação</a></li>
                <li><a href="#alteracoes">Alterações nesta política</a></li>
                <li><a href="#contato">Contato</a></li>
              </ol>
            </nav>

            <section id="sobre" className="legal-section">
              <h2>1. O que são cookies</h2>
              <p>
                Cookies são <strong>pequenos arquivos de texto</strong> armazenados no seu navegador ou
                dispositivo quando você visita um site ou usa uma aplicação web. Eles permitem que o site
                reconheça seu dispositivo, lembre preferências e ofereça uma experiência mais personalizada e
                segura.
              </p>
              <p>
                Esta Política descreve quais cookies e tecnologias similares (web beacons, pixels, local
                storage, session storage) a LifeUp utiliza no site{' '}
                <a href="https://lifyup.com.br" target="_blank" rel="noopener noreferrer">lifyup.com.br</a>{' '}
                e como você pode controlá-los.
              </p>
            </section>

            <section id="por-que" className="legal-section">
              <h2>2. Por que usamos cookies</h2>
              <p>Utilizamos cookies para os seguintes propósitos:</p>
              <ul>
                <li><strong>Garantir o funcionamento</strong> básico do site (autenticação, segurança, balanceamento de carga);</li>
                <li><strong>Lembrar preferências</strong> (idioma, tema, programa selecionado);</li>
                <li><strong>Analisar uso</strong> de forma agregada para melhorar conteúdo e desempenho;</li>
                <li><strong>Medir campanhas</strong> de marketing (com seu consentimento prévio);</li>
                <li><strong>Prevenir fraudes</strong> e detectar atividade suspeita.</li>
              </ul>
            </section>

            <section id="tipos" className="legal-section">
              <h2>3. Tipos de cookies utilizados</h2>

              <h3>3.1 Estritamente necessários</h3>
              <p>
                Indispensáveis ao funcionamento do site. Sem eles, serviços como login, segurança e checkout
                não funcionariam. <strong>Não exigem consentimento</strong> (base legal: legítimo interesse /
                execução de contrato).
              </p>

              <h3>3.2 Funcionais / Preferências</h3>
              <p>
                Permitem lembrar escolhas (idioma, tema escuro, programa preferido). Sua ausência não impede o
                uso do site, mas reduz a personalização.
              </p>

              <h3>3.3 Analíticos / Desempenho</h3>
              <p>
                Coletam estatísticas agregadas (páginas visitadas, tempo de sessão, dispositivo) para entender
                como o site é usado e onde podemos melhorar. <strong>Exigem consentimento</strong>.
              </p>

              <h3>3.4 Marketing / Publicidade</h3>
              <p>
                Medem efetividade de campanhas e podem ser usados por nossos parceiros (Meta, Google) para exibir
                anúncios em outras plataformas. <strong>Exigem consentimento explícito</strong> e podem ser
                revogados a qualquer momento.
              </p>
            </section>

            <section id="tabela" className="legal-section">
              <h2>4. Lista detalhada de cookies</h2>
              <p>A tabela abaixo lista os principais cookies/tecnologias que podemos usar:</p>

              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Categoria</th>
                    <th>Fornecedor</th>
                    <th>Finalidade</th>
                    <th>Retenção</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>auth_token</td>
                    <td>Necessário</td>
                    <td>LifeUp</td>
                    <td>Manter usuário autenticado</td>
                    <td>Sessão / 30 dias</td>
                  </tr>
                  <tr>
                    <td>refresh_token</td>
                    <td>Necessário</td>
                    <td>LifeUp</td>
                    <td>Renovar sessão sem novo login</td>
                    <td>90 dias</td>
                  </tr>
                  <tr>
                    <td>lifeup_program</td>
                    <td>Funcional</td>
                    <td>LifeUp</td>
                    <td>Lembrar último programa selecionado</td>
                    <td>1 ano</td>
                  </tr>
                  <tr>
                    <td>lifeup_consent</td>
                    <td>Necessário</td>
                    <td>LifeUp</td>
                    <td>Guardar suas preferências de cookies</td>
                    <td>1 ano</td>
                  </tr>
                  <tr>
                    <td>_ga, _ga_*</td>
                    <td>Analítico</td>
                    <td>Google Analytics 4</td>
                    <td>Estatísticas agregadas de uso</td>
                    <td>2 anos</td>
                  </tr>
                  <tr>
                    <td>_gid</td>
                    <td>Analítico</td>
                    <td>Google Analytics</td>
                    <td>Distinção de usuários</td>
                    <td>24 horas</td>
                  </tr>
                  <tr>
                    <td>_fbp</td>
                    <td>Marketing</td>
                    <td>Meta (Facebook)</td>
                    <td>Medição de campanhas e remarketing</td>
                    <td>3 meses</td>
                  </tr>
                  <tr>
                    <td>fr</td>
                    <td>Marketing</td>
                    <td>Meta (Facebook)</td>
                    <td>Anúncios personalizados</td>
                    <td>3 meses</td>
                  </tr>
                  <tr>
                    <td>sentry-trace</td>
                    <td>Necessário</td>
                    <td>Sentry</td>
                    <td>Diagnóstico de erros e desempenho</td>
                    <td>Sessão</td>
                  </tr>
                </tbody>
              </table>
              <p className="legal-updated">
                Lista atualizada periodicamente conforme nossas integrações evoluem.
              </p>
            </section>

            <section id="terceiros" className="legal-section">
              <h2>5. Cookies de terceiros</h2>
              <p>
                Alguns parceiros podem definir cookies próprios ao prestar serviços para a LifeUp. Esses
                fornecedores têm suas próprias políticas de privacidade e cookies, que recomendamos consultar:
              </p>
              <ul>
                <li>
                  <strong>Google Analytics / Ads:</strong>{' '}
                  <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer">
                    policies.google.com/technologies/cookies
                  </a>
                </li>
                <li>
                  <strong>Meta (Facebook/Instagram):</strong>{' '}
                  <a href="https://www.facebook.com/policies/cookies/" target="_blank" rel="noopener noreferrer">
                    facebook.com/policies/cookies
                  </a>
                </li>
                <li>
                  <strong>Stripe:</strong>{' '}
                  <a href="https://stripe.com/cookies-policy/legal" target="_blank" rel="noopener noreferrer">
                    stripe.com/cookies-policy/legal
                  </a>
                </li>
                <li>
                  <strong>Sentry:</strong>{' '}
                  <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer">
                    sentry.io/privacy
                  </a>
                </li>
              </ul>
            </section>

            <section id="mobile" className="legal-section">
              <h2>6. Cookies no aplicativo móvel</h2>
              <p>
                Os aplicativos LifeUp para <strong>iOS</strong> e <strong>Android</strong> não utilizam cookies
                tradicionais. Em vez disso, usamos tecnologias equivalentes para armazenamento local:
              </p>
              <ul>
                <li><strong>Secure Storage (Keychain/Keystore):</strong> armazenamento criptografado de tokens de autenticação.</li>
                <li><strong>SQLite local:</strong> cache de dados para uso offline.</li>
                <li><strong>Identificadores publicitários (IDFA/AAID):</strong> usados <em>somente</em> mediante autorização (ATT no iOS).</li>
                <li><strong>Firebase Cloud Messaging:</strong> token de notificações push (mediante consentimento).</li>
              </ul>
              <p>
                Você pode gerenciar essas permissões nas configurações do sistema operacional do seu dispositivo.
              </p>
            </section>

            <section id="gerenciar" className="legal-section">
              <h2>7. Como gerenciar e remover cookies</h2>
              <p>Você pode controlar cookies de várias formas:</p>

              <h3>7.1 No banner da LifeUp</h3>
              <p>
                Na primeira visita ao site, exibimos um banner para você <strong>aceitar, recusar ou
                personalizar</strong> os cookies não-essenciais. Você pode revisitar suas escolhas a qualquer
                momento clicando no link &quot;Preferências de cookies&quot; no rodapé.
              </p>

              <h3>7.2 No seu navegador</h3>
              <p>Cada navegador permite gerenciar cookies. Consulte os guias oficiais:</p>
              <ul>
                <li>
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">
                    Google Chrome
                  </a>
                </li>
                <li>
                  <a href="https://support.mozilla.org/pt-BR/kb/ative-e-desative-os-cookies-que-os-sites-usam" target="_blank" rel="noopener noreferrer">
                    Mozilla Firefox
                  </a>
                </li>
                <li>
                  <a href="https://support.apple.com/pt-br/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">
                    Safari
                  </a>
                </li>
                <li>
                  <a href="https://support.microsoft.com/pt-br/microsoft-edge" target="_blank" rel="noopener noreferrer">
                    Microsoft Edge
                  </a>
                </li>
              </ul>

              <div className="info-box">
                <p>
                  <strong>Observação:</strong> bloquear cookies estritamente necessários pode impedir o
                  funcionamento de partes do site, como login e checkout.
                </p>
              </div>
            </section>

            <section id="consentimento" className="legal-section">
              <h2>8. Consentimento e revogação</h2>
              <p>
                Em conformidade com a <strong>LGPD</strong>, o <strong>GDPR</strong> e demais legislações
                aplicáveis, cookies não essenciais (analíticos e de marketing) só são ativados após seu
                consentimento explícito.
              </p>
              <p>Você pode, a qualquer momento:</p>
              <ul>
                <li>Revogar consentimento pelo link &quot;Preferências de cookies&quot; no rodapé;</li>
                <li>Limpar cookies já armazenados pelas configurações do seu navegador;</li>
                <li>Solicitar suporte: <a href="mailto:dpo@lifyup.com.br">dpo@lifyup.com.br</a>.</li>
              </ul>
            </section>

            <section id="alteracoes" className="legal-section">
              <h2>9. Alterações nesta política</h2>
              <p>
                Podemos atualizar esta Política de Cookies para refletir novas tecnologias, integrações ou
                exigências legais. Mudanças significativas serão comunicadas com destaque no site. A data da
                última atualização sempre constará no topo desta página.
              </p>
            </section>

            <section id="contato" className="legal-section">
              <h2>10. Contato</h2>
              <p>Dúvidas sobre esta política ou sobre o uso de cookies pela LifeUp:</p>
              <ul>
                <li><strong>DPO:</strong> <a href="mailto:dpo@lifyup.com.br">dpo@lifyup.com.br</a></li>
                <li><strong>Suporte:</strong> <a href="mailto:suporte@lifyup.com.br">suporte@lifyup.com.br</a></li>
                <li><strong>Política de Privacidade:</strong> <a href="/privacidade">/privacidade</a></li>
                <li><strong>LGPD:</strong> <a href="/lgpd">/lgpd</a></li>
                <li><strong>Termos de Uso:</strong> <a href="/termos">/termos</a></li>
              </ul>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CookiesPage;
